import React, {useEffect, useState} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./Footer.scss";
import {useTranslation} from "react-i18next";
import TOSPopup from "../../Components/TOS/TOS";

interface FooterProps {
    onTosClick?: () => void;
}


export default function Footer({ onTosClick }: FooterProps) {
    const year = new Date().getFullYear();
    const [tosOpen, setTosOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const { pathname, hash: urlHash } = useLocation();
    const { t } = useTranslation();

    useEffect(() => {
        if (pathname === "/" && urlHash === "#cta") {
            const timer = setTimeout(() => {
                document.getElementById("cta")?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [pathname, urlHash]);

    const validPaths = [
        "/",
        "/lavori",
        "/servizi",
        "/servizi/video",
        "/servizi/video/short-form",
        "/servizi/video/long-form",
        "/servizi/grafica",
        "/servizi/grafica/youtube-thumbnail",
        "/servizi/grafica/minecraft",
        "/servizi/grafica/logos",
        "/servizi/grafica/drawings",
        "/servizi/development",
        "/servizi/development/minecraft",
        "/servizi/development/web",
        "/faq",
    ];

    if (!validPaths.includes(pathname)) {
        return null;
    }


    const scrollTo = (where: string) => (e: React.MouseEvent) => {
        e.preventDefault();

        // Contatti: vai alla Home e poi alla sezione CTA
        if (where === "/#cta") {
            if (pathname !== "/") {
                navigate("/#cta");
            } else {
                document.getElementById("cta")?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }
            return;
        }

        // Home e Servizi: vai in alto
        if (where === "/" || where === "/servizi") {
            navigate(where);

            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            return;
        }

        // Altri link
        const id = where.startsWith("#") ? where.slice(1) : where;
        const element = document.getElementById(id);

        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        } else {
            navigate(where.startsWith("/") ? where : `/${where}`);
        }
    };

    const menuLinks = [
        { label: t("footer.navButon.home"), href: "/" },
        { label: t("footer.navButon.services"), href: "/servizi" },
        { label: t("footer.navButon.contact"), href: "/#cta" },
    ];

    return (
        <footer className="ender-footer">
            <div className="ender-footer__line" />

            <div className="ender-footer__container">
                <div className="ender-footer__grid">
                    {/* Brand */}
                    <div className="ender-footer__brand">
                        <a href="#" className="ender-footer__logo">
                            <img
                                src="https://ik.imagekit.io/n4dmneisyc/enderclub/logo.jpg"
                                alt="EnderClub"
                            />

                            <span>
                <strong>Ender</strong>Club
              </span>
                        </a>

                        <p>
                            {t("footer.subtitle.row1")}
                            <br />
                            {t("footer.subtitle.row2")}
                        </p>
                    </div>

                    {/* Menu */}
                    <div className="ender-footer__column">
                        <h4>{t("footer.navButon.name")}</h4>

                        <ul>
                            {menuLinks.map((link) => (
                                <Link to={link.href} onClick={scrollTo(link.href)}>
                                    <span className="ender-footer__arrow">›</span>
                                    {link.label}
                                </Link>
                            ))}

                            <li>
                                <button
                                    type="button"
                                    onClick={() => setTosOpen(true)}
                                    className="ender-footer__tos"
                                >
                                      <span className="ender-footer__tos-icon">
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden="true"
                                        >
                                          <path
                                              d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z"
                                              stroke="currentColor"
                                              strokeWidth="1.7"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                          />
                                          <path
                                              d="M14 2V8H20"
                                              stroke="currentColor"
                                              strokeWidth="1.7"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                          />
                                          <path
                                              d="M8 13H16M8 17H14"
                                              stroke="currentColor"
                                              strokeWidth="1.7"
                                              strokeLinecap="round"
                                          />
                                        </svg>
                                      </span>

                                    <span>{t("footer.navButon.TOS")}</span>
                                </button>
                            </li>
                        </ul>
                    </div>

                    <TOSPopup open={tosOpen} onClose={() => setTosOpen(false)} />

                    {/* Contatti */}
                    <div className="ender-footer__column">
                        <h4>{t("footer.contact.name")}</h4>

                        <ul>
                            <li>
                                <a
                                    href="https://discord.gg/Xd4fEecuzm"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t("footer.contact.discord")}
                                </a>
                            </li>

                            <li>
                                <a href="mailto:info@enderclub.it">
                                    {t("footer.contact.email")}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Bottom */}
                <div className="ender-footer__bottom">
                    <p>
                        © {year} EnderClub. {t("footer.rights")}
                    </p>
                    <p className="ender-footer__bottom__firma" onClick={() => {window.open("https://t.me/STambu09", "_blank");}}>
                        {t("footer.by")} STambu09
                    </p>

                </div>
            </div>
        </footer>
    );
}
